/**
 * Hook para usar el servicio de WebSocket
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { ChatMessage } from '../types';
import webSocketService from '../services/webSocketService';
import configService from '../services/configService';
import conversationSaverService from '../services/conversationSaverService';

export interface UseWebSocketReturn {
  messages: ChatMessage[];
  isConnected: boolean;
  isConnecting: boolean;
  sendAudioMessage: (audioBlob: Blob) => Promise<void>;
  sendTextMessage: (text: string) => Promise<void>;
  error: string | null;
}

export function useWebSocket(shouldConnect: boolean = false): UseWebSocketReturn {
  // Cargar mensajes históricos desde webSocketService al iniciar
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    return webSocketService.getChatHistory();
  });
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasInitializedRef = useRef(false);
  const lastHistoryLengthRef = useRef(0);

  useEffect(() => {
    // Configurar callbacks
    const messageCallback = (_message: ChatMessage) => {
      // El mensaje ya está en chathistory del webSocketService
      // Actualizamos el estado local con el historial completo
      const updatedHistory = webSocketService.getChatHistory();
      
      // Actualizar siempre que haya cambios en el historial
      // Ya no hay consolidación, así que solo verificamos cambios de longitud
      const currentLength = updatedHistory.length;
      
      // Actualizar si la longitud cambió (nuevo mensaje agregado)
      if (currentLength !== lastHistoryLengthRef.current) {
        lastHistoryLengthRef.current = currentLength;
        setMessages([...updatedHistory]);
      }
    };

    webSocketService.onMessage(messageCallback);

    // Callback para cuando se limpia el historial
    const historyClearedCallback = () => {
      const updatedHistory = webSocketService.getChatHistory();
      lastHistoryLengthRef.current = updatedHistory.length;
      setMessages([...updatedHistory]);
    };

    webSocketService.onChatHistoryCleared(historyClearedCallback);

    // El audio ahora se maneja internamente en webSocketService con cola
    // El callback onAudio se usa solo para notificaciones, no para reproducir directamente
    const audioCallback = (_audioUrl: string) => {
      // El audio ya se está reproduciendo en webSocketService.tryPlayNextAudio()
      // Este callback se mantiene por compatibilidad pero no hace nada aquí
    };

    webSocketService.onAudio(audioCallback);

    // Inicializar el ref con la longitud actual
    lastHistoryLengthRef.current = webSocketService.getChatHistory().length;

    // Cleanup: desregistrar callbacks cuando el componente se desmonte
    return () => {
      webSocketService.offMessage();
      webSocketService.offAudio();
      webSocketService.offChatHistoryCleared();
    };
  }, []);

  useEffect(() => {
    // Solo intentar conectar si shouldConnect es true
    if (!shouldConnect) {
      // Resetear el flag si shouldConnect cambia a false
      hasInitializedRef.current = false;
      setIsConnecting(false);
      setIsConnected(false);
      return;
    }

    // console.trace(`[VoiceDebug] [useWebSocket] Stack trace:`);

    // Evitar múltiples inicializaciones
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;
    let isMounted = true;
    
    // Establecer estado de conexión
    setIsConnecting(true);
    setIsConnected(false);
    
    // Registrar callback para cuando la conexión esté completamente lista
    const handleConnectionReady = () => {
      if (isMounted) {
        setIsConnecting(false);
        setIsConnected(true);
        setError(null);
        // El mensaje inicial se envía automáticamente desde webSocketService.ts cuando la conexión está lista
      }
    };
    
    webSocketService.onConnectionReady(handleConnectionReady);
    
    // Inicializar conexión
    webSocketService
      .initialize()
      .then(() => {
        // La conexión puede estar lista inmediatamente o después
        // El callback handleConnectionReady se llamará cuando esté lista
      })
      .catch((err) => {
        hasInitializedRef.current = false; // Permitir reintento en caso de error
        if (isMounted) {
          // console.warn('Error al conectar WebSocket (modo desarrollo):', err);
          // En modo desarrollo/testing, simular conexión exitosa después de un delay
          const config = configService.getConfig();
          if (config.testingPhase || config.testingWebSocket) {
            setTimeout(() => {
              if (isMounted) {
                setIsConnected(true);
                setError(null);
              }
            }, 2000);
          } else {
            setError(err.message || 'Error al conectar WebSocket');
            setIsConnected(false);
          }
        }
      });

    return () => {
      isMounted = false;
      webSocketService.offConnectionReady();
      // Solo desconectar si shouldConnect cambia a false
      if (!shouldConnect) {
        webSocketService.disconnect();
        hasInitializedRef.current = false;
        setIsConnecting(false);
        setIsConnected(false);
      }
    };
  }, [shouldConnect]);

  const sendAudioMessage = useCallback(async (audioBlob: Blob) => {
    try {
      // Incrementar contador de audio
      conversationSaverService.incrementAudioCount();
      
      await webSocketService.sendAudioMessage(audioBlob);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Error al enviar mensaje de audio');
    }
  }, []);

  const sendTextMessage = useCallback(async (text: string) => {try {
      // Incrementar contador de texto
      conversationSaverService.incrementTextCount();await webSocketService.sendTextMessage(text);
      // No actualizar manualmente aquí, el callback onMessage ya lo hace
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Error al enviar mensaje de texto');
    }
  }, []);

  return {
    messages,
    isConnected,
    isConnecting,
    sendAudioMessage,
    sendTextMessage,
    error,
  };
}


